import dayjs, { type Dayjs } from '@/config/dayjs';
import { API_DATE_FORMAT, APP_DATE_FORMAT } from '@/constants/dateFormats';

export interface PointInTimeHistoricalValue {
  effectiveDate: string | Dayjs;
  value: number;
}

export interface HistoricalValueRange {
  FromDate: string; // MM/DD/YYYY
  ToDate: string; // MM/DD/YYYY
  Value: number;
}

function toDayjs(value: string | Dayjs): Dayjs | null {
  if (typeof value !== 'string') {
    return value.isValid() ? value : null;
  }

  const parsed = dayjs(value, APP_DATE_FORMAT, true);
  return parsed.isValid() ? parsed : null;
}

function dateKey(d: Dayjs) {
  return d.format(API_DATE_FORMAT);
}

export function deriveHistoricalValueRanges(
  entries: PointInTimeHistoricalValue[],
): { ranges: HistoricalValueRange[]; duplicateDateKeys: string[] } {
  const normalized = entries
    .map((e) => ({
      date: toDayjs(e.effectiveDate),
      value: e.value,
    }))
    .filter((e): e is { date: Dayjs; value: number } => Boolean(e.date));

  const duplicates = new Set<string>();
  const seen = new Set<string>();
  normalized.forEach((e) => {
    const key = dateKey(e.date);
    if (seen.has(key)) duplicates.add(key);
    seen.add(key);
  });

  const sorted = [...normalized].sort((a, b) => a.date.diff(b.date, 'day'));

  const ranges: HistoricalValueRange[] = sorted.map((current) => {
    const from = current.date.startOf('day');
    const to = from;

    return {
      FromDate: from.format(APP_DATE_FORMAT),
      ToDate: to.format(APP_DATE_FORMAT),
      Value: current.value,
    };
  });

  return { ranges, duplicateDateKeys: [...duplicates] };
}
